<script setup>
import TrackedKeywordImportantToFollowCell from '@frontJs/components/tables/cells/projectManagement/seo/TrackedKeywordImportantToFollowCell.vue';
import CaretDownIcon from '@iconify-vue/ph/caret-down';
import DisplayLineChartAction from '@js/components/actions/DisplayLineChartAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import ExportButton from '@js/components/buttons/ExportButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import LinkCell from '@js/components/tables/cells/LinkCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import PillsCell from '@js/components/tables/cells/PillsCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const {
    tenant,
    websiteVariants,
    targetableGoogleWebsites,
    importantToFollow = null,
} = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    websiteVariants: {
        type: Array,
        required: true,
    },
    targetableGoogleWebsites: {
        type: Array,
        required: true,
    },
    importantToFollow: {
        type: Boolean,
        required: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    setGlobalFilter,
    availableFilters,
    filters,
    setFilters,
    selectionIds,
    exportData,
    updateData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'keyword',
            header: () => 'Mot-clé',
            cell: (props) =>
                props.row.original.keyword
                    ? h(TextCell, {
                          text: props.row.original.keyword,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'intents',
            header: () => 'Intentions',
            cell: (props) =>
                h(PillsCell, {
                    pillValues: props.row.original.formattedIntents.map(
                        (intent) => ({
                            value: intent.abbreviation,
                            title: intent.label,
                            type: 'primary',
                        }),
                    ),
                }),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'google_websites.name',
            header: () => 'Site Google',
            cell: (props) =>
                props.row.original.googleWebsiteName
                    ? h(PillCell, {
                          pillValue: props.row.original.googleWebsiteName,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'website_variants.name',
            header: () => 'Site',
            cell: (props) =>
                props.row.original.websiteVariantName
                    ? h(PillCell, {
                          pillValue: props.row.original.websiteVariantName,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'position',
            header: () => 'Position',
            headerAlign: 'right',
            cell: (props) =>
                h(
                    NumberCell,
                    {
                        number: props.row.original.position,
                        align: 'right',
                    },
                    props.row.original.positionChartData
                        ? {
                              actions: () =>
                                  h(DisplayLineChartAction, {
                                      labels: props.row.original
                                          .positionChartData.labels,
                                      datasets: [
                                          {
                                              group: 'Position',
                                              label: 'Position',
                                              backgroundColor: '#4d65b9',
                                              borderColor: '#4d65b9',
                                              pointRadius: 0,
                                              pointHoverRadius: 0,
                                              fill: {
                                                  target: 'start',
                                                  above: '#e4e8f4',
                                              },
                                              tension: 0.4,
                                              data: props.row.original
                                                  .positionChartData.data,
                                              labels: props.row.original
                                                  .positionChartData.labels,
                                              yAxisID: 'y',
                                          },
                                      ],
                                      title: 'Positions',
                                      withDecimalsAxis1: false,
                                      reverseAxis1: true,
                                      minAxis1: 1,
                                      maxAxis1: 100,
                                  }),
                          }
                        : {},
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'position_variation_30_days',
            header: () => 'Var 30J',
            cell: (props) =>
                props.row.original.position30DaysAgo
                    ? h(NumberCell, {
                          number: props.row.original.positionVariation30Days,
                          withPlus: true,
                          color:
                              props.row.original.positionVariation30Days > 0
                                  ? 'green'
                                  : props.row.original.positionVariation30Days <
                                      0
                                    ? 'red'
                                    : undefined,
                      })
                    : undefined,
        }),
        columnHelper.accessor(() => null, {
            id: 'position_variation_6_months',
            header: () => 'Var 6M',
            cell: (props) =>
                props.row.original.position6MonthsAgo
                    ? h(NumberCell, {
                          number: props.row.original.positionVariation6Months,
                          withPlus: true,
                          color:
                              props.row.original.positionVariation6Months > 0
                                  ? 'green'
                                  : props.row.original
                                          .positionVariation6Months < 0
                                    ? 'red'
                                    : undefined,
                      })
                    : undefined,
        }),
        columnHelper.accessor(() => null, {
            id: 'position_variation_12_months',
            header: () => 'Var 12M',
            cell: (props) =>
                props.row.original.position12MonthsAgo
                    ? h(NumberCell, {
                          number: props.row.original.positionVariation12Months,
                          withPlus: true,
                          color:
                              props.row.original.positionVariation12Months > 0
                                  ? 'green'
                                  : props.row.original
                                          .positionVariation12Months < 0
                                    ? 'red'
                                    : undefined,
                      })
                    : undefined,
        }),
        columnHelper.accessor(() => null, {
            id: 'searchVolume',
            header: () => 'Volume',
            headerAlign: 'right',
            cell: (props) =>
                props.row.original.searchVolume
                    ? h(
                          NumberCell,
                          {
                              number: props.row.original.searchVolume,
                              align: 'right',
                          },
                          props.row.original.searchVolumeChartData
                              ? {
                                    actions: () =>
                                        h(DisplayLineChartAction, {
                                            labels: props.row.original
                                                .searchVolumeChartData.labels,
                                            datasets: [
                                                {
                                                    group: 'Volume de recherche',
                                                    label: 'Volume de recherche',
                                                    backgroundColor: '#4d65b9',
                                                    borderColor: '#4d65b9',
                                                    pointRadius: 0,
                                                    pointHoverRadius: 0,
                                                    fill: {
                                                        target: 'origin',
                                                        above: '#e4e8f4',
                                                    },
                                                    tension: 0.4,
                                                    data: props.row.original
                                                        .searchVolumeChartData
                                                        .data,
                                                    labels: props.row.original
                                                        .searchVolumeChartData
                                                        .labels,
                                                    yAxisID: 'y',
                                                },
                                            ],
                                            title: 'Volumes de recherche',
                                            withDecimalsAxis1: false,
                                            axis1BeginAtZero: true,
                                        }),
                                }
                              : {},
                      )
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'url',
            header: () => 'URL',
            cell: (props) =>
                props.row.original.url
                    ? h(LinkCell, {
                          text: props.row.original.url,
                          href: props.row.original.url,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'importantToFollow',
            header: () => 'Important',
            cell: (props) =>
                props.row.original.importantToFollow !== null &&
                props.row.original.importantToFollow !== undefined
                    ? h(TrackedKeywordImportantToFollowCell, {
                          tenant,
                          seoTrackedKeyword: props.row.original,
                          callback: updateData,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'date',
            header: () => 'Dernière MAJ',
            cell: (props) =>
                props.row.original.date
                    ? h(TextCell, {
                          text: props.row.original.date,
                      })
                    : null,
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            props.row.getCanExpand()
                                ? h(ActionButton, {
                                      size: 'small',
                                      type: 'secondary',
                                      square: true,
                                      title: props.row.getIsExpanded()
                                          ? 'Réduire'
                                          : 'Développer',
                                      callback: () =>
                                          props.row.toggleExpanded(),
                                      icon: CaretDownIcon,
                                      rotateIcon: props.row.getIsExpanded(),
                                  })
                                : null,
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.seo.postPositions', {
        tenant: tenant.id,
    }),
    exportUrl: route('front.projectManagement.seo.postExportPositions', {
        tenant: tenant.id,
    }),
    columnPinning: {
        right: ['actions'],
    },
    columnVisibility: {
        'google_websites.name': websiteVariants.length > 1,
        'website_variants.name': targetableGoogleWebsites.length > 1,
    },
    data: {
        importantToFollow,
    },
    selectionIdKey: 'id',
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :set-global-filter="setGlobalFilter"
        :available-filters="availableFilters"
        :filters="filters"
        :set-filters="setFilters"
        :loading="loading"
    >
        <template #actions>
            <ExportButton
                :csv-export-callback="() => exportData('csv')"
                :xlsx-export-callback="() => exportData('xlsx')"
                :disabled="selectionIds.length === 0"
            />
        </template>
    </DataTableLayout>
</template>
